/**
 * Write operations against Simkl.
 *
 * Shapes taken from the Python client's tested implementation rather than from
 * the docs, which have been wrong on every endpoint checked so far. Notably
 * `add-to-list` needs `to` on each item, not at the top level — the documented
 * placement returns 400 "Missed to parameter".
 */
import type { SimklClient } from "@/api/client";

export interface EpisodeTarget {
  simklId: number;
  season: number;
  episode: number;
}

type ShowBody = {
  ids: { simkl: number };
  seasons?: { number: number; episodes?: { number: number }[] }[];
  to?: string;
};

/*
 * There is no season-level write here any more. Simkl accepts one, but the
 * screen cannot update itself from it — a season number says nothing about
 * which episodes changed — so the sheet expands a season into its episodes
 * before queueing. One write shape, and the optimistic update has something
 * concrete to apply.
 */

function episodeBody(targets: readonly EpisodeTarget[]): { shows: ShowBody[] } {
  const byShow = new Map<number, Map<number, number[]>>();
  for (const target of targets) {
    const seasons = byShow.get(target.simklId) ?? new Map<number, number[]>();
    const episodes = seasons.get(target.season) ?? [];
    episodes.push(target.episode);
    seasons.set(target.season, episodes);
    byShow.set(target.simklId, seasons);
  }
  return {
    shows: [...byShow].map(([simkl, seasons]) => ({
      ids: { simkl },
      seasons: [...seasons].map(([number, episodes]) => ({
        number,
        episodes: episodes.map((n) => ({ number: n })),
      })),
    })),
  };
}

/** Marks specific episodes watched. Idempotent: re-marking is a no-op at Simkl. */
export async function markWatched(
  client: SimklClient,
  targets: readonly EpisodeTarget[],
): Promise<void> {
  if (targets.length === 0) return;
  await client.request("/sync/history", { method: "POST", body: episodeBody(targets) });
}

/** Removes specific episodes from history. */
export async function markUnwatched(
  client: SimklClient,
  targets: readonly EpisodeTarget[],
): Promise<void> {
  if (targets.length === 0) return;
  await client.request("/sync/history/remove", { method: "POST", body: episodeBody(targets) });
}

/**
 * Adds a show to a list.
 *
 * `to` goes on each item. The documented top-level placement returns
 * 400 "Missed to parameter" — confirmed against the live API.
 */
export async function addToList(
  client: SimklClient,
  simklId: number,
  to: "watching" | "plantowatch" | "completed" | "hold" | "dropped",
): Promise<void> {
  await client.request("/sync/add-to-list", {
    method: "POST",
    body: { shows: [{ ids: { simkl: simklId }, to }] },
  });
}

/**
 * Removes a show from the account entirely: off every list, history wiped.
 *
 * `/sync/history/remove` with no `seasons` is the whole-show form. Verified
 * against the live API by adding a throwaway show, removing it, and confirming
 * it left the library — the docs describe this endpoint as episode-only.
 */
export async function removeShow(client: SimklClient, simklId: number): Promise<void> {
  await client.request("/sync/history/remove", {
    method: "POST",
    body: { shows: [{ ids: { simkl: simklId } }] },
  });
}
